import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import SqrtInput from "../../../../components/inputs/SqrtInput";

// CubeDiagonalTask.js
// Zadanie: oblicz długość przekątnej sześcianu o krawędzi a.
// Wzór: d = a * sqrt(3)

// Losowe długości krawędzi (cm)
const EDGES = [2, 3, 4, 5, 6, 8, 10];

export function generateCubeDiagonalTask() {
  const a = EDGES[Math.floor(Math.random() * EDGES.length)];
  const core = { a };
  return {
    type: "CubeDiagonalTask",
    ...core,
    data: core,
  };
}

export function generateTask() {
  return generateCubeDiagonalTask();
}

function unwrapTask(taskOrWrapper) {
  if (!taskOrWrapper) return taskOrWrapper;
  return taskOrWrapper.data ? taskOrWrapper.data : taskOrWrapper;
}

// --- TREŚĆ ZADANIA ---

export function renderTask(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { a } = task;

  return (
    <p>
      Dany jest sześcian o krawędzi <InlineMath math={`a = ${a}\\,\\text{cm}`} />.
      Oblicz długość przekątnej tego sześcianu. Wynik podaj w postaci wyrażenia z
      pierwiastkiem.
    </p>
  );
}

// --- INPUT (quiz + zadanie) ---
// ✅ jednostka w suffix inputu (jak w zadaniach z procentu składanego)

export function renderInput(taskOrWrapper, userAnswer, onChange, disabled) {
  unwrapTask(taskOrWrapper);

  return (
    <div className="flex items-center gap-2">
      <InlineMath math="d =" />
      <SqrtInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="d"
        suffixLatex="\text{cm}"
      />
    </div>
  );
}

// --- PODGLĄD ODPOWIEDZI UCZNIA ---
// ✅ podgląd z "d = ..." + jednostka

export function renderUserAnswer(userAnswer) {
  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer?.raw ?? userAnswer?.parsed ?? "";

  if (!raw || String(raw).trim() === "") {
    return <i>(brak odpowiedzi)</i>;
  }

  const text = String(raw).trim();
  return <InlineMath math={`d = ${text}\\,\\text{cm}`} />;
}

// --- PARSOWANIE WYRAŻENIA Z PIERWIASTKIEM ---

function evaluateExpression(expr) {
  if (expr == null) return null;
  const cleaned = String(expr)
    .replace(/\s/g, "")
    .replace(/\u200b/g, "")
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, "")
    .replace(/,/g, "."); // przecinek -> kropka

  const normalized = cleaned
    // \sqrt{3}, sqrt{3}, sqrt(3)
    .replace(/\\\\sqrt\{([\d.]+)\}/g, "Math.sqrt($1)")
    .replace(/\\sqrt\{([\d.]+)\}/g, "Math.sqrt($1)")
    .replace(/sqrt\{([\d.]+)\}/g, "Math.sqrt($1)")
    .replace(/sqrt\(([\d.]+)\)/g, "Math.sqrt($1)")
    // 2√3, √3
    .replace(/(\d*)√(\d+(\.\d+)?)/g, (_, a, b) => `${a || "1"}*Math.sqrt(${b})`)
    .replace(/√(\d+(\.\d+)?)/g, (_, b) => `Math.sqrt(${b})`);

  try {
    const val = Function('"use strict"; return (' + normalized + ');')();
    if (typeof val !== "number" || !isFinite(val)) return null;
    return val;
  } catch {
    return null;
  }
}

// --- SPRAWDZENIE ---

export function checkAnswer(taskOrWrapper, userAnswer) {
  const task = unwrapTask(taskOrWrapper);
  const { a } = task;

  if (!userAnswer) return false;

  const raw =
    typeof userAnswer === "string"
      ? userAnswer
      : userAnswer.raw ?? userAnswer.parsed ?? "";

  const numeric = evaluateExpression(raw);
  if (numeric == null) return false;

  const correct = a * Math.sqrt(3);
  return Math.abs(numeric - correct) < 1e-2;
}

// --- POPRAWNA ODPOWIEDŹ ---
// ✅ tylko wynik (bez "d = a√3 = ..."), InlineMath (do lewej)

export function renderAnswer(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  const { a } = task;

  return <InlineMath math={`${a}\\sqrt{3}\\,\\text{cm}`} />;
}

// --- WYJAŚNIENIE ---

export function renderExplanation(taskOrWrapper) {
  const task = unwrapTask(taskOrWrapper);
  if (!task) return null;

  const { a } = task;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong>{" "}
        Rozpatrujemy przekątną ściany sześcianu <InlineMath math="d_p" />.
        Ściana jest kwadratem o boku <InlineMath math="a" />, więc:
        <BlockMath
          math={String.raw`
            \begin{aligned}
            d_p^2 &= a^2 + a^2 \\
                  &= 2a^2 \\
            d_p   &= a\sqrt{2}
            \end{aligned}
          `}
        />
      </div>

      <div>
        <strong>Krok 2:</strong>{" "}
        Przekątna sześcianu <InlineMath math="d" /> tworzy trójkąt
        prostokątny z przekątną ściany <InlineMath math="d_p" /> oraz
        krawędzią <InlineMath math="a" />. Stosujemy ponownie twierdzenie
        Pitagorasa:
        <BlockMath
          math={String.raw`
            \begin{aligned}
            d^2 &= d_p^2 + a^2 \\
                &= 2a^2 + a^2 \\
                &= 3a^2 \\
            d   &= \sqrt{3a^2} \\
                &= a\sqrt{3}
            \end{aligned}
          `}
        />
      </div>

      <div>
        <strong>Krok 3:</strong>{" "}
        Podstawiamy wartość krawędzi{" "}
        <InlineMath math={`a = ${a}\\,\\text{cm}`} /> i zapisujemy wynik:
        <BlockMath
          math={String.raw`
            \begin{aligned}
            d &= a\sqrt{3} \\
              &= ${a}\sqrt{3}
            \end{aligned}
          `}
        />
        <BlockMath math={`d = ${a}\\sqrt{3}\\,\\text{cm}`} />
      </div>
    </div>
  );
}
